import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

/**
 * @fileOverview Feature-aware navigation with dynamic route and query parameter injection
 * @description Service that handles navigation from the navigation menu to application features.
 * Dynamically extracts and injects route/query parameters from localStorage based on feature
 * configuration, enabling seamless feature access with required context data.
 * 
 * @module navigationController
 */

/**
 * @service MenuNavigationControllerService
 * @description Service that orchestrates navigation from feature menus to application routes.
 * Handles dynamic route parameter and query parameter injection from localStorage,
 * enabling features to receive necessary context (businessID, adminStatus, etc.) during navigation.
 *
 * Core Responsibilities:
 * - Navigate users to feature routes with dynamic parameters
 * - Extract route parameters from localStorage based on feature configuration
 * - Extract query parameters from localStorage based on feature configuration
 * - Handle special cases (e.g., Provider business "Your Customers" feature)
 * - Support three navigation modes: route params only, query params only, or combined
 * 
 * Navigation Flow:
 * 1. Accept a feature object with navigation metadata
 * 2. Validate the feature has a valid routePath
 * 3. Build route params from localStorage using feature.params array
 * 4. Build query params from localStorage using feature.queryParams array
 * 5. Apply special business logic for role/context-specific parameter values
 * 6. Execute navigation using Angular Router with appropriate params
 * 
 * Feature Configuration Expected:
 * ```typescript
 * interface Feature {
 *   name: string;                 // Display name of the feature
 *   routePath: string;            // Angular route path (e.g., '/pond', '/customers')
 *   params?: string[];            // Keys to extract from localStorage for route params
 *   queryParams?: string[];       // Keys to extract from localStorage for query params
 * }
 * ```
 * 
 * localStorage Keys Commonly Used:
 * - 'businessID': Current business ID
 * - 'loginBusinessCategory': Business category (Farm, Provider, etc.)
 * - 'isAdmin': Admin status in context
 * - Custom keys specific to application state
 * 
 * Special Handling:
 * - **isAdmin Param**: For "Your Customers" feature in Provider business, sets to 'Customers'
 *   instead of 'Business' to indicate customer view vs internal business admin view
 * 
 * @example
 * // In a sidebar/menu component
 * constructor(private navigation: MenuNavigationControllerService) {}
 * 
 * onFeatureClick(feature: any) {
 *   // feature: { name: 'Pond', routePath: '/pond', params: ['businessID'] }
 *   this.navigation.navigate(feature);
 *   // Navigates to: /pond/[businessID from localStorage]
 * }
 * 
 * @example
 * // In a component with multiple parameters
 * const feature = {
 *   name: 'Your Customers',
 *   routePath: '/customers',
 *   params: ['businessID', 'isAdmin'],
 *   queryParams: ['filter', 'sort']
 * };
 * this.navigation.navigate(feature);
 * // Navigates to: /customers/[businessID]/[isAdmin]?filter=value&sort=value
 */
@Injectable({ 
  providedIn: 'root' 
})
export class NavigationMenuRouteControllerService {

  /**
   * @constructor
   * @description Initializes the MenuNavigationControllerService with the Angular Router.
   * 
   * @param {Router} router - Angular Router service for executing navigation
   */
  constructor(private router: Router) {}

  /**
   * @method navigate
   * @description Navigates to a feature route with dynamically injected parameters from localStorage.
   * Extracts route params and query params based on the feature configuration, applies
   * any special business logic, and executes navigation using Angular Router.
   * 
   * Parameter Resolution Process:
   * 1. **Validate Route**: Check that feature.routePath exists; log error if missing
   * 2. **Extract Route Params**: For each key in feature.params[], get value from localStorage
   * 3. **Extract Query Params**: For each key in feature.queryParams[], get value from localStorage
   * 4. **Apply Special Logic**: Handle special cases like isAdmin for Provider businesses
   * 5. **Navigate**: Choose navigation mode based on available params
   * 
   * Navigation Modes:
   * - **Mode 1 - Query + Route Params**: Navigate with both route and query params
   *   - Route: `[routePath, businessID, isAdmin]`
   *   - Query: `{ filter: value, sort: value }`
   *   - Example: `/customers/123/admin?status=active`
   * 
   * - **Mode 2 - Route Params Only**: Navigate with route params, no query params
   *   - Route: `[routePath, businessID, isAdmin]`
   *   - Example: `/customers/123/admin`
   * 
   * - **Mode 3 - No Params**: Navigate to route without any params
   *   - Route: `[routePath]`
   *   - Example: `/dashboard`
   * 
   * Special Handling - Provider "Your Customers" Feature:
   * When navigating from a Provider business admin to "Your Customers" feature,
   * the isAdmin parameter is set to 'Customers' instead of 'Business' to indicate
   * that the feature should render in customer-viewing mode rather than admin mode.
   * 
   * @public
   * @param {any} feature - Feature object containing navigation metadata.
   *   Expected properties:
   *   - feature.name (string): Display name of the feature (used in special case logic)
   *   - feature.routePath (string): Angular route path (e.g., '/pond', '/customers')
   *   - feature.params (string[], optional): localStorage keys for route parameters
   *   - feature.queryParams (string[], optional): localStorage keys for query parameters
   * @returns {void}
   * 
   * @throws {void} Does not throw; logs error to console if routePath is missing
   * 
   * @example
   * // Basic navigation to dashboard
   * const dashboardFeature = {
   *   name: 'Dashboard',
   *   routePath: '/dashboard'
   * };
   * this.navigate(dashboardFeature);
   * // Result: Navigates to /dashboard
   * 
   * @example
   * // Navigation with route parameters
   * const pondFeature = {
   *   name: 'Pond',
   *   routePath: '/pond',
   *   params: ['businessID', 'isAdmin']
   * };
   * // Assuming localStorage.getItem('businessID') = '123'
   * // Assuming localStorage.getItem('isAdmin') = 'Business'
   * this.navigate(pondFeature);
   * // Result: Navigates to /pond/123/Business
   * 
   * @example
   * // Navigation with route and query parameters
   * const customersFeature = {
   *   name: 'Your Customers',
   *   routePath: '/customers',
   *   params: ['businessID', 'isAdmin'],
   *   queryParams: ['status', 'sort']
   * };
   * // Assuming localStorage has: businessID=456, isAdmin=Customers, status=active, sort=name
   * // Additionally assuming businessCategory = 'Provider' (special case applied)
   * this.navigate(customersFeature);
   * // Result: Navigates to /customers/456/Customers?status=active&sort=name
   * // Note: isAdmin set to 'Customers' due to Provider business special case
   * 
   * @example
   * // Navigation with only query parameters
   * const reportsFeature = {
   *   name: 'Reports',
   *   routePath: '/reports',
   *   queryParams: ['period', 'format']
   * };
   * // Assuming localStorage has: period=monthly, format=pdf
   * this.navigate(reportsFeature);
   * // Result: Navigates to /reports?period=monthly&format=pdf
   */
  public navigate(feature) {
    if (!feature.routePath) {
      console.error('Invalid feature link:', feature);
      return;
    }
    const routeParams: { [key: string]: any } = {};
    const queryParams: { [key: string]: any } = {};

    // Populate dynamic route params from localStorage
    if (Array.isArray(feature.params)) {
      feature.params.forEach(paramKey => {
        const value = localStorage.getItem(paramKey);
        if (value) {
          routeParams[paramKey] = value;
        } else if (paramKey == 'isAdmin') {
          routeParams[paramKey] = localStorage.getItem('loginBusinessCategory') == 'Provider' && feature.name == 'Your Customers' ? 'Customers' : 'Business'
        }
      });
    }
    // Populate dynamic query params from localStorage
    if (Array.isArray(feature.queryParams)) {
      feature.queryParams.forEach(paramKey => {
        const value = localStorage.getItem(paramKey);
        if (value) {
          queryParams[paramKey] = value;
        }
      });
    }
    // Navigate based on available params
    if (Object.keys(queryParams).length > 0) {
      // Case 1: Navigate with route params + query params
      this.router.navigate([feature.routePath, routeParams.businessID, routeParams.isAdmin], { queryParams });
    } else if (Object.keys(routeParams).length > 0) {
      // Case 2: Navigate with route params only
      this.router.navigate([feature.routePath, routeParams.businessID, routeParams.isAdmin]);
    } else {
      // Case 3: Navigate without params
      this.router.navigate([feature.routePath]);
    }
  }
}